/**
 * Shared fetch-based SSE stream reader for the AI-native canvas streaming endpoints (spec 044
 * chat-api.md / paste-action-streaming-api.md). Native `EventSource` can't send a POST body,
 * so both `aiChatService.ts` and `aiCanvasActionService.ts` read the response body manually.
 */

export interface SseEvent {
  event: string;
  data: any;
}

export interface SseStreamHandlers {
  onEvent: (event: SseEvent) => void;
  onError?: (error: unknown) => void;
}

export async function readSseStream(response: Response, handlers: SseStreamHandlers): Promise<void> {
  if (!response.ok || !response.body) {
    handlers.onError?.(new Error(`Request failed with status ${response.status}`));
    return;
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  try {
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      let boundary = buffer.indexOf('\n\n');
      while (boundary !== -1) {
        const rawEvent = buffer.slice(0, boundary);
        buffer = buffer.slice(boundary + 2);
        parseAndDispatch(rawEvent, handlers);
        boundary = buffer.indexOf('\n\n');
      }
    }
  } catch (error) {
    handlers.onError?.(error);
  }
}

function parseAndDispatch(rawEvent: string, handlers: SseStreamHandlers): void {
  let eventName = 'message';
  let dataLine = '';

  for (const line of rawEvent.split('\n')) {
    if (line.startsWith('event:')) {
      eventName = line.slice(6).trim();
    } else if (line.startsWith('data:')) {
      dataLine += line.slice(5).trim();
    }
  }

  if (!dataLine) return;

  try {
    handlers.onEvent({ event: eventName, data: JSON.parse(dataLine) });
  } catch {
    // Malformed chunk — skip rather than crash the whole stream.
  }
}
